import React, { useState, useEffect, useRef, useCallback } from 'react';
import { arrayOf, shape, string, func } from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import { NamedLink, ReloveIconPhotos } from '../../components';
import css from './MessagesSection.css';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { createSlug } from '../../util/urlHelpers';

const MessagesSection = props => {
  const { messages, otherUser, currentUser, onSendMessage, loadMoreMessages, listing } = props;

  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [initialLoad, setInitialLoad] = useState(true);

  const handleSendMessage = async () => {
    if (newMessage.trim()) {
      setIsSending(true);
      try {
        await onSendMessage(newMessage);
        setNewMessage('');
      } catch (error) {
        console.error('Error sending message:', error);
      } finally {
        setIsSending(false);
      }
    }
  };

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      if (isNearBottom || initialLoad) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: initialLoad ? 'auto' : 'smooth'
        });
      }
    }
  }, [initialLoad]);

  // Track the previous messages length to detect new messages
  const prevMessagesLengthRef = useRef(messages.length);
  const isUserScrollingRef = useRef(false);

  // Only scroll to bottom on initial load or when sending a new message
  useEffect(() => {
    if (initialLoad) {
      scrollToBottom();
      setInitialLoad(false);
    } else if (messages.length > prevMessagesLengthRef.current && !isUserScrollingRef.current) {
      // Only scroll if the new message is from the current user
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.sender_id === currentUser.id.uuid) {
        scrollToBottom();
      }
    }
    prevMessagesLengthRef.current = messages.length;
  }, [scrollToBottom, initialLoad, messages, currentUser.id.uuid]);

  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const debouncedHandleLoadMoreMessages = useCallback(
    debounce(() => {
      if (messagesContainerRef.current && !loadingMore) {
        const container = messagesContainerRef.current;
        const currentScrollTop = container.scrollTop;
        const threshold = 50;

        if (currentScrollTop <= threshold) {
          setLoadingMore(true);
          const previousHeight = container.scrollHeight;
          const previousScroll = container.scrollTop;

          loadMoreMessages()
            .then(() => {
              requestAnimationFrame(() => {
                if (container) {
                  const newHeight = container.scrollHeight;
                  const heightDifference = newHeight - previousHeight;
                  container.scrollTop = previousScroll + heightDifference;
                }
                setLoadingMore(false);
              });
            })
            .catch(() => {
              setLoadingMore(false);
            });
        }
      }
    }, 200),
    [loadingMore, loadMoreMessages]
  );

  const handleScroll = useCallback((e) => {
    isUserScrollingRef.current = true;
    debouncedHandleLoadMoreMessages(e);
    // Reset the user scrolling flag after a short delay
    setTimeout(() => {
      isUserScrollingRef.current = false;
    }, 150);
  }, [debouncedHandleLoadMoreMessages]);

  // Remove the wheel handler as it's causing issues
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  const formatMessageDate = timestamp => {
    const messageDate = new Date(timestamp);
    const now = new Date();

    // Reset time to compare only the date parts
    const messageDateOnly = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
    const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterdayDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);

    let dateLabel;
    if (messageDateOnly.getTime() === nowDateOnly.getTime()) { // Check if the message is from today
      dateLabel = 'hoy';
    } else if (messageDateOnly.getTime() === yesterdayDateOnly.getTime()) { // Check if the message is from yesterday
      dateLabel = 'ayer';
    } else {
      dateLabel = format(messageDate, 'dd/MM/yyyy', { locale: es });
    }

    const timeLabel = format(messageDate, 'HH:mm', { locale: es });
    return `${dateLabel} ${timeLabel}`;
  };

  return (
    <div className={css.root}>
      <div className={css.infoMessageDesktop}>
        <span className={css.infoIcon}>🔒</span>
        Recuerda que tus compras dentro de Gloset están protegidas y tienen garantía
      </div>
      <ul className={`${css.messageList} ${css.fixedHeight}`} ref={messagesContainerRef}>
        {messages.map((message, index) => {
          if (!message || !message.sender_id || !message.message || !message.timestamp) {
            console.error('Invalid message object:', message);
            return null;
          }

          const isCurrentUserSender = message.sender_id === currentUser.id.uuid;
          const otherUserInitials = otherUser.attributes.profile.displayName
            .split(' ')
            .map(name => name[0])
            .join('');

          return isCurrentUserSender ? (
            <li
              key={message.id || `message-${index}`}
              className={css.messageItemSender}
            >
              <div>
                <div className={css.messageHeaderSender}>
                  <span className={css.senderName}>Tu</span>
                  <span className={css.timestamp}>
                    {formatMessageDate(message.timestamp)}
                  </span>
                </div>
                <p className={css.messageContentSender}>{message.message}</p>
              </div>
            </li>
          ) : (
            <div className={css.otherUserMessageContainer} key={message.id || `message-${index}`}>
              <NamedLink name="ProfilePage" params={{ id: otherUser.id.uuid }} className={css.receiverImage}>
                {otherUser.profileImage?.attributes?.variants?.default?.url ? (
                  <img
                    src={otherUser.profileImage.attributes.variants.default.url}
                    alt="User"
                    className={css.receiverImage}
                  />
                ) : (
                  <div className={css.messageBubbleInitials}>{otherUserInitials}</div>
                )}
              </NamedLink>
              <li className={css.messageItemReceiver}>
                <div>
                  <div className={css.messageHeaderReceiver}>
                    <span className={css.senderName}>{otherUser.attributes.profile.displayName}</span>
                    <span className={css.timestamp}>
                      {formatMessageDate(message.timestamp)}
                    </span>
                  </div>
                  <p className={css.messageContentReceiver}>{message.message}</p>
                </div>
              </li>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </ul>
      <div className={`${css.newMessageContainer} ${
        (listing.author.id.uuid === currentUser.id.uuid || 
         listing.attributes.publicData.sold || 
         listing.attributes.publicData.available !== 'Disponible') 
        ? css.bottomPosition 
        : css.hiddenButtons
      }`}>
        <input
          type="text"
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          placeholder="Escribe tu mensaje..."
          className={css.newMessageInput}
        />
        <button onClick={handleSendMessage} className={css.sendButton} disabled={isSending}>
          {isSending ? 'Enviando...' : 'Enviar'}
        </button>
        {listing && listing.author.id.uuid === currentUser.id.uuid && (
          <NamedLink
            className={css.editListingButton}
            name="EditListingPage"
            params={{
              id: listing.id.uuid,
              slug: createSlug(listing.attributes.title),
              type: listing.type,
              edit: 'description'
            }}
          >
            <ReloveIconPhotos className={css.sendIconPhotos} />
          </NamedLink>
        )}
      </div>
    </div>
  );
};

MessagesSection.propTypes = {
  messages: arrayOf(
    shape({
      id: string.isRequired,
      message: string.isRequired,
      timestamp: string.isRequired,
      sender_id: string.isRequired,
    })
  ).isRequired,
  otherUser: shape({
    id: string.isRequired,
    attributes: shape({
      profile: shape({
        displayName: string.isRequired,
      }),
    }),
  }).isRequired,
  currentUser: shape({
    id: string.isRequired,
    attributes: shape({
      profile: shape({
        displayName: string.isRequired,
      }),
    }),
  }).isRequired,
  listing: shape({
    id: shape({
      uuid: string.isRequired,
    }).isRequired,
    author: shape({
      id: shape({
        uuid: string.isRequired,
      }).isRequired,
    }).isRequired,
    attributes: shape({
      title: string.isRequired,
    }).isRequired,
    type: string,
  }).isRequired,
  onSendMessage: func.isRequired,
  loadMoreMessages: func.isRequired,
};

export default MessagesSection;